import { z } from 'zod';
import { ErrorEnvelope, UserRef } from './common.js';
import * as common from './common.js';
import * as enums from './enums.js';
import * as identity from './identity.js';
import * as work from './work.js';
import * as requests from './requests.js';
import * as collaboration from './collaboration.js';
import * as deliveryContracts from './delivery.js';
import * as reports from './reports.js';
import * as admin from './admin.js';
import type { RouteDefinition } from './route.js';
import { routes } from './routes.js';
import { mcpTools } from './mcp.js';
import { MindSnapshot } from './mind-export.js';

export const API_VERSION = '1.0.0';
export const API_PREFIX = '/api/v1';

/** Named component schemas, collected while converting operations. */
const components: Record<string, unknown> = {};

/** Registers every exported object/union schema under its export name so OpenAPI uses $refs. */
function registerComponents() {
  const modules = [enums, common, identity, work, requests, collaboration, deliveryContracts, reports, admin];
  if (!z.globalRegistry.has(UserRef)) z.globalRegistry.add(UserRef, { id: 'UserRef' });
  for (const mod of modules) {
    for (const [name, value] of Object.entries(mod)) {
      if (!(value instanceof z.ZodType)) continue;
      const kind = (value as z.ZodType).def.type;
      if (!['object', 'union', 'pipe', 'string', 'enum'].includes(kind)) continue;
      if (!z.globalRegistry.has(value as z.ZodType)) z.globalRegistry.add(value as z.ZodType, { id: name });
    }
  }
}

function jsonSchema(schema: z.ZodType, io: 'input' | 'output'): Record<string, unknown> {
  const result = z.toJSONSchema(schema, { target: 'draft-2020-12', io, unrepresentable: 'any', metadata: z.globalRegistry }) as Record<string, unknown>;
  delete result.$schema;
  const defs = result.$defs as Record<string, unknown> | undefined;
  delete result.$defs;
  const rewrite = (value: unknown): unknown =>
    JSON.parse(JSON.stringify(value).split('"#/$defs/').join('"#/components/schemas/'));
  if (defs) for (const [defId, def] of Object.entries(defs)) components[defId] ??= rewrite(def);
  const id = z.globalRegistry.get(schema)?.id;
  if (id) {
    components[id] ??= rewrite(result);
    return { $ref: `#/components/schemas/${id}` };
  }
  return rewrite(result) as Record<string, unknown>;
}

/** Self-contained JSON Schema (no component references), as MCP hosts require. */
export function inlineJsonSchema(schema: z.ZodType, io: 'input' | 'output'): Record<string, unknown> {
  const result = z.toJSONSchema(schema, { target: 'draft-2020-12', io, unrepresentable: 'any', metadata: z.registry(), reused: 'inline' }) as Record<string, unknown>;
  delete result.$schema;
  return result;
}

const toOpenApiPath = (path: string) => `${API_PREFIX}${path.replace(/:([A-Za-z]+)/g, '{$1}')}`;

function parameters(route: RouteDefinition) {
  const params: Array<Record<string, unknown>> = [];
  if (route.params) {
    const shape = (route.params as z.ZodObject).shape as Record<string, z.ZodType>;
    for (const [name, schema] of Object.entries(shape)) {
      params.push({ name, in: 'path', required: true, schema: jsonSchema(schema, 'input') });
    }
  }
  if (route.query) {
    const shape = (route.query as z.ZodObject).shape as Record<string, z.ZodType>;
    for (const [name, schema] of Object.entries(shape)) {
      params.push({ name, in: 'query', required: !schema.safeParse(undefined).success, schema: jsonSchema(schema, 'input') });
    }
  }
  if (route.idempotent) {
    params.push({
      name: 'Idempotency-Key',
      in: 'header',
      required: false,
      description: 'Retry-safe key scoped to the actor and operation; reuse with a different payload returns 409 IDEMPOTENCY_CONFLICT.',
      schema: { type: 'string', minLength: 8, maxLength: 200 },
    });
  }
  return params;
}

const errorResponse = (description: string) => ({
  description,
  content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
});

/** Builds the OpenAPI 3.1 document from the shared route registry. */
export function buildOpenApiDocument(): Record<string, unknown> {
  registerComponents();
  const paths: Record<string, Record<string, unknown>> = {};
  const entries = Object.entries(routes as Record<string, RouteDefinition>).sort(([a], [b]) => a.localeCompare(b));
  for (const [, route] of entries) {
    const path = toOpenApiPath(route.path);
    paths[path] ??= {};
    const responses: Record<string, unknown> = {
      [String(route.successStatus)]: route.response
        ? { description: 'Success', content: { 'application/json': { schema: jsonSchema(route.response, 'output') } } }
        : { description: 'No content' },
      '400': { $ref: '#/components/responses/MalformedRequest' },
      '401': { $ref: '#/components/responses/Unauthenticated' },
      '403': { $ref: '#/components/responses/Forbidden' },
      '404': { $ref: '#/components/responses/NotFound' },
      '409': { $ref: '#/components/responses/Conflict' },
      '422': { $ref: '#/components/responses/InvalidState' },
      '429': { $ref: '#/components/responses/RateLimited' },
      '503': { $ref: '#/components/responses/Unavailable' },
    };
    paths[path][route.method.toLowerCase()] = {
      operationId: route.operationId,
      summary: route.summary,
      tags: [route.tag],
      security: route.auth === 'session' ? [{ session: [] }, { bearer: [] }] : [],
      parameters: parameters(route),
      ...(route.body
        ? { requestBody: { required: true, content: { 'application/json': { schema: jsonSchema(route.body, 'input') } } } }
        : {}),
      responses,
    };
  }
  // Machine read-only lane is intentionally not a human REST/MCP operation.
  paths[`${API_PREFIX}/mind-export/snapshot`] = {
    get: {
      operationId: 'mindExportSnapshot', summary: 'Bounded effective-company-only project snapshot (disabled by default)',
      tags: ['Mind export'], security: [{ mindExport: [] }],
      responses: {
        '200': { description: 'Complete repeatable-read snapshot; validate hashes/counts before reconciliation', content: { 'application/json': { schema: jsonSchema(MindSnapshot, 'output') } } },
        ...Object.fromEntries([400, 401, 404, 413, 429, 503].map((code) => [String(code), { description: 'No authoritative snapshot; do not reconcile removals' }])),
      },
    },
  };
  return {
    openapi: '3.1.0',
    info: {
      title: 'Vammo Work Tracker API',
      version: API_VERSION,
      description:
        'Domain API shared by the web UI, MCP tools and workers. All operations apply the same authorization and domain rules.',
    },
    servers: [{ url: '/' }],
    components: {
      securitySchemes: {
        mindExport: { type: 'http', scheme: 'bearer', description: 'Dedicated read-export-only machine token; never a human OAuth credential' },
        session: { type: 'apiKey', in: 'cookie', name: '__Host-vwt_session' },
        bearer: { type: 'http', scheme: 'bearer', description: 'OAuth access token for MCP/agent clients' },
      },
      responses: {
        MalformedRequest: errorResponse('Malformed request'),
        Unauthenticated: errorResponse('Missing or expired identity'),
        Forbidden: errorResponse('Visible but forbidden'),
        NotFound: errorResponse('Absent or inaccessible'),
        Conflict: errorResponse('Version, idempotency or stale-generation conflict'),
        InvalidState: errorResponse('Invalid fields or state'),
        RateLimited: errorResponse('Rate limited'),
        Unavailable: errorResponse('Required capability unavailable'),
      },
      schemas: { ErrorEnvelope: jsonSchema(ErrorEnvelope, 'output'), ...Object.fromEntries(Object.entries(components).sort(([a], [b]) => a.localeCompare(b))) },
    },
    paths,
  };
}

/** MCP tool catalog (name, description, JSON Schemas, annotations). */
export function buildMcpToolCatalog() {
  return Object.values(mcpTools)
    .map((tool) => ({
      name: tool.name,
      title: tool.title,
      description: tool.description,
      scope: tool.scope,
      annotations: tool.annotations,
      inputSchema: inlineJsonSchema(tool.input, 'input'),
      // MCP requires an object-typed outputSchema; unions of objects get an explicit type.
      outputSchema: { type: 'object', ...inlineJsonSchema(tool.output, 'output') },
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Generates the typed fetch client source from the route registry. */
export function generateClientSource(): string {
  const entries = Object.entries(routes as Record<string, RouteDefinition>).sort(([a], [b]) => a.localeCompare(b));
  const lines: string[] = [];
  lines.push('// Generated by `pnpm contracts:generate`. Do not edit by hand; CI fails on drift.');
  lines.push("import type { z } from 'zod';");
  lines.push("import type { routes } from '../routes.js';");
  lines.push('');
  lines.push('type R = typeof routes;');
  lines.push('type In<T> = [NonNullable<T>] extends [never] ? undefined : NonNullable<T> extends z.ZodType ? z.input<NonNullable<T>> : undefined;');
  lines.push('type Out<T> = [NonNullable<T>] extends [never] ? undefined : NonNullable<T> extends z.ZodType ? z.output<NonNullable<T>> : undefined;');
  lines.push('');
  lines.push('export interface ApiOperations {');
  for (const [name] of entries) {
    lines.push(`  ${name}: { params: In<R['${name}']['params']>; query: In<R['${name}']['query']>; body: In<R['${name}']['body']>; response: Out<R['${name}']['response']> };`);
  }
  lines.push('}');
  lines.push('');
  lines.push('export const operations = {');
  for (const [name, route] of entries) {
    lines.push(`  ${name}: { method: '${route.method}', path: '${route.path}', idempotent: ${route.idempotent ? 'true' : 'false'} },`);
  }
  lines.push('} as const;');
  lines.push('');
  lines.push('export type OperationName = keyof ApiOperations;');
  lines.push('');
  return lines.join('\n');
}
